/* ============================================================
   DIETMONSTA · Cart drawer

   Open/close, remove a pack, and re-render after any cart change.

   The drawer's own markup is re-fetched from the server via the Section
   Rendering API rather than patched in JS. Totals, the progress rail and
   the checkout gate are all computed by Liquid, so there is exactly one
   implementation of those rules and the drawer can never disagree with
   the cart page.
   ============================================================ */

const DM_SECTION = 'dm-cart-drawer';

class DMCartDrawer extends HTMLElement {
  connectedCallback() {
    this.#bind();
    // Re-bind after the section is swapped out.
    document.addEventListener('dm:cart-updated', () => this.#refresh());
  }

  #bind() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-dm-close]')) return this.close();
      const rm = event.target.closest('[data-dm-remove]');
      if (rm) return this.#remove(rm);
    });

    if (!this.__keyHandler) {
      this.__keyHandler = (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
      };
      document.addEventListener('keydown', this.__keyHandler);
    }
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    const close = this.querySelector('[data-dm-close].dm-drawer__close');
    if (close) close.focus();
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  async #remove(btn) {
    const keys = (btn.dataset.dmRemove || '').split(',').filter(Boolean);
    if (!keys.length) return;
    btn.disabled = true;

    const updates = {};
    keys.forEach((k) => {
      updates[k] = 0;
    });

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error('update_failed');
      await this.#refresh();
    } catch {
      btn.disabled = false;
    }
  }

  /* Pull fresh markup for this section and swap the panel in place.

     Refreshes can overlap: adding to cart fires one right before the
     drawer opens, and — the bug this guards against — a customer who
     types their postcode in that same instant triggers a second one when
     the delivery picker saves. Two in-flight fetches race, and nothing
     stops the slower one (the stale, pre-postcode HTML) from winning and
     landing *after* the faster one, silently wiping the delivery result
     and postcode field the customer just filled in — no error, the
     checkout gate just stays stuck. A monotonic token makes only the
     most recently REQUESTED refresh allowed to apply, regardless of which
     fetch actually resolves first. */
  #refreshToken = 0;

  async #refresh() {
    const token = ++this.#refreshToken;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}?sections=${DM_SECTION}`);
      if (token !== this.#refreshToken) return; // superseded by a newer refresh
      if (!res.ok) return;
      const data = await res.json();
      if (token !== this.#refreshToken) return;
      const html = data[DM_SECTION];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('dm-cart-drawer');
      if (!fresh) return;

      const wasOpen = this.hasAttribute('open');

      /* Capture the progress state before the swap. Replacing the markup
         destroys the old nodes, so a CSS transition has nothing to animate
         from — the rail would jump. These two snapshots let us replay the
         change deliberately afterwards. */
      const prevFill = this.querySelector('.dm-rail__fill')?.style.width || null;
      const prevTruck = this.querySelector('.dm-rail__truck')?.style.left || null;
      const prevReached = {};
      this.querySelectorAll('[data-milestone]').forEach((el) => {
        prevReached[el.dataset.milestone] = el.dataset.reached === 'true';
      });

      this.innerHTML = fresh.innerHTML;

      this.#replayProgress(prevFill, prevTruck, prevReached);
      Array.from(fresh.attributes).forEach((a) => {
        if (a.name !== 'open') this.setAttribute(a.name, a.value);
      });
      if (wasOpen) this.setAttribute('open', '');

      DMCartDrawer.syncBadges(parseInt(fresh.dataset.itemCount, 10) || 0);
    } catch {
      /* leave the current markup in place — stale beats blank */
    }
  }

  /* Animate the rail from where it was, and celebrate only a milestone
     that has just been earned — not every one already met, which would
     make the whole rail twitch on each quantity change. */
  #replayProgress(prevFill, prevTruck, prevReached) {
    /* Replay a value that the server rendered at its final state, so it
       transitions from where it was instead of snapping. Reading
       offsetWidth commits the old value first. This must stay synchronous:
       deferring it to requestAnimationFrame left the rail stuck at the
       previous value in a backgrounded tab, where rAF does not run. */
    const replay = (el, prop, prev) => {
      if (!el || prev === null || prev === el.style[prop]) return false;
      const target = el.style[prop];
      el.style[prop] = prev;
      void el.offsetWidth;
      el.style[prop] = target;
      return true;
    };

    replay(this.querySelector('.dm-rail__fill'), 'width', prevFill);

    const truck = this.querySelector('.dm-rail__truck');
    if (replay(truck, 'left', prevTruck)) {
      DMCartDrawer.flash(truck, 'is-moving');
    }

    this.querySelectorAll('[data-milestone]').forEach((el) => {
      const key = el.dataset.milestone;
      const now = el.dataset.reached === 'true';
      if (now && prevReached[key] === false) {
        DMCartDrawer.flash(el, 'is-just-reached');
      }
    });
  }

  /* Add a class for the length of one animation, then take it off again.

     animationend alone is not enough: it never fires if the animation does
     not run — a background tab, or reduced-motion, where the rules are
     disabled entirely — and the class would stick. The timer guarantees
     cleanup either way. */
  static flash(el, cls) {
    el.classList.add(cls);
    const clear = () => {
      el.classList.remove(cls);
      clearTimeout(timer);
    };
    /* Comfortably past the 700ms drive, so the class is never pulled
       while the van is still moving. */
    const timer = setTimeout(clear, 950);
    el.addEventListener('animationend', clear, { once: true });
  }

  /* Keep Dawn's header cart bubble in step with the drawer. */
  static syncBadges(count) {
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
      el.textContent = count;
    });
    const bubble = document.querySelector('.cart-count-bubble');
    if (bubble) bubble.hidden = count === 0;
  }

  static get instance() {
    return document.querySelector('dm-cart-drawer');
  }
}

if (!customElements.get('dm-cart-drawer')) {
  customElements.define('dm-cart-drawer', DMCartDrawer);
}

/* ---- Global wiring ---------------------------------------------------- */

document.addEventListener('click', (event) => {
  // Any link to /cart opens the drawer instead of navigating — except when
  // the shopper explicitly asked for a new tab, or JS-free fallback matters.
  const link = event.target.closest('a[href$="/cart"], a[href*="/cart?"]');
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const drawer = DMCartDrawer.instance;
  if (!drawer) return;
  event.preventDefault();
  drawer.open();
});

/* Builders dispatch this after a successful /cart/add.js */
document.addEventListener('dm:cart-added', () => {
  const drawer = DMCartDrawer.instance;
  if (!drawer) return;
  document.dispatchEvent(new CustomEvent('dm:cart-updated'));
  drawer.open();
});
